'use client'

import { useEffect, useMemo, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { CheckCircle2, Lock, Mail, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCurrentUser } from '@/hooks/useCurrentUser'

const DEFAULT_ALLOWED = [
  '/experience',
  '/checkout',
  '/thank-you',
  '/order',
  '/product',
  '/publication-share',
  '/setup',
  '/start',
  '/login',
  '/signup',
]

function isAllowed(pathname: string, allow: string[]) {
  return allow.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

type Step = 'password' | 'otp' | 'verifying_otp' | 'saving_password'

export function CompleteAccountAccessGate({
  allow = DEFAULT_ALLOWED,
  title = 'Completa tu cuenta',
  message = 'Crea una contraseña para acceder rápido y seguro.',
}: {
  allow?: string[]
  title?: string
  message?: string
}) {
  const { user, scope, refreshUser } = useCurrentUser() as any
  const pathname = usePathname()
  const router = useRouter()

  const needsCompletion = useMemo(() => scope === 'checkout_only', [scope])

  const shouldGate = useMemo(() => {
    if (!needsCompletion) return false
    if (!pathname) return false
    return !isAllowed(pathname, allow)
  }, [needsCompletion, pathname, allow])

  const [step, setStep] = useState<Step>('password')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const isLoading = step === 'saving_password' || step === 'verifying_otp'
  const currentEmail = String(user?.email || email || '').trim().toLowerCase()

  useEffect(() => {
    if (!shouldGate) return

    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prev
    }
  }, [shouldGate])

  async function switchAccount() {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        cache: 'no-store',
      })
    } catch {}

    window.location.href = '/login'
  }

  async function startOtp(forEmail?: string) {
    setErr(null)

    const e = String(forEmail || currentEmail || '').trim().toLowerCase()

    if (!e || e.length < 5) {
      setErr('Escribe tu email para enviarte el código.')
      setStep('otp')
      return
    }

    const res = await fetch('/api/auth/otp/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: e }),
    })

    const data = await res.json().catch(() => null)

    if (!res.ok || data?.ok === false) {
      setStep('otp')
      setErr(data?.error || 'No se pudo enviar el código.')
      return
    }

    setStep('otp')
  }

  async function verifyOtp() {
    setErr(null)

    if (code.length !== 6) {
      setErr('Escribe el código de 6 dígitos.')
      return
    }

    setStep('verifying_otp')

    const res = await fetch('/api/auth/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })

    const data = await res.json().catch(() => null)

    if (!res.ok || data?.ok !== true) {
      setStep('otp')
      setErr(data?.error || 'Código inválido o expirado.')
      return
    }

    setCode('')
    setErr(null)
    setStep('password')
  }

  async function completeAccount() {
    setErr(null)

    if (password.length < 8) {
      setErr('Tu contraseña debe tener al menos 8 caracteres.')
      return
    }

    if (password !== confirm) {
      setErr('Las contraseñas no coinciden.')
      return
    }

    setStep('saving_password')

    const res = await fetch('/api/auth/complete-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })

    const data = await res.json().catch(() => null)

    if (data?.error === 'REAUTH_REQUIRED') {
      setPassword('')
      setConfirm('')
      setErr(null)
      await startOtp()
      return
    }

    if (!res.ok || data?.ok !== true) {
      setStep('password')
      setErr(data?.error || 'No se pudo activar tu cuenta.')
      return
    }

    await refreshUser()
    router.refresh()

    setStep('password')
    setPassword('')
    setConfirm('')
    setCode('')
    setErr(null)
  }

  if (!shouldGate) return null

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md" />

      <div className="relative z-10 flex h-full w-full items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card/95 p-6 text-card-foreground shadow-2xl">
          {step !== 'otp' && step !== 'verifying_otp' && (
            <>
              <div className="flex items-start gap-3 items-center">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <Lock className="h-5 w-5" />
                </div>
                <div className="text-lg font-semibold">{title}</div>
              </div>

              <div>                  
                  <div className="mt-4 text-sm text-muted-foreground">
                    {message}
                  </div>

                  {user?.email && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Cuenta: <span className="font-medium">{user.email}</span>
                    </div>
                  )}
                </div>

              <div className="mt-5 space-y-3">
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crea tu contraseña"
                  type="password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />

                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirma tu contraseña"
                  type="password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Por seguridad, si tu acceso temporal ya expiró, te pediremos confirmar tu email antes de guardar la contraseña.
                </div>
              </div>
            </>
          )}

          {(step === 'otp' || step === 'verifying_otp') && (
            <>
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <Mail className="h-5 w-5" />
                </div>

                <div>
                  <div className="text-lg font-semibold">
                    Confirma tu email
                  </div>

                  <div className="mt-2 text-sm text-muted-foreground">
                    Tu acceso temporal expiró. Para proteger tu cuenta, enviamos un código de verificación antes de crear tu contraseña.
                  </div>
                </div>
              </div>

              {!user?.email && (
                <div className="mt-5">
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    type="email"
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>
              )}

              <div className="mt-5">
                <Input
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="Código de 6 dígitos"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {err && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {err}
            </div>
          )}

          {step !== 'otp' && step !== 'verifying_otp' && (
            <div className="mt-5 flex flex-wrap gap-2">
              <Button className="w-full" onClick={completeAccount} disabled={isLoading}>
                {step === 'saving_password' ? 'Activando…' : 'Activar cuenta'}
              </Button>
            </div>
          )}

          {(step === 'otp' || step === 'verifying_otp') && (
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={verifyOtp}
                disabled={isLoading || code.length !== 6}
              >
                {step === 'verifying_otp' ? 'Verificando…' : 'Verificar código'}
              </Button>

              <Button
                variant="outline"
                onClick={() => startOtp()}
                disabled={isLoading}
              >
                Reenviar código
              </Button>
            </div>
          )}

          {step === 'password' && (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground hidden">
              <CheckCircle2 className="h-4 w-4" />
              Sin códigos si completas tu cuenta ahora.
            </div>
          )}

          {step === 'password' && (
            <div className="mt-2 pt-3 text-center">
              <button
                type="button"
                onClick={switchAccount}
                disabled={isLoading}
                className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50"
              >
                ¿No es tu cuenta? Cambiar de cuenta
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}